import { useState, type FormEvent } from 'react';
import {
  Button,
  ConfirmDialog,
  Input,
  Modal,
  PageHeader,
  RowMenu,
} from '../../components';
import { ApiError } from '../../lib/api';
import { useCrud } from '../../lib/useCrud';

type Role = {
  id: string;
  slug: string;
  name: string;
  description: string | null;
};

const PROTECTED = new Set(['admin', 'sales', 'staff', 'readonly']);

type FormState = { slug: string; name: string; description: string };
const toForm = (r?: Role): FormState => ({
  slug: r?.slug ?? '',
  name: r?.name ?? '',
  description: r?.description ?? '',
});
const toBody = (f: FormState) => ({
  slug: f.slug.trim(),
  name: f.name.trim(),
  description: f.description.trim() || null,
});

export function RolesPage() {
  const crud = useCrud<Role, ReturnType<typeof toBody>, Partial<ReturnType<typeof toBody>>>({
    key: 'hrm-roles',
    path: '/hrm/roles',
    listQuery: 'limit=100',
  });
  const [editing, setEditing] = useState<Role | 'new' | null>(null);
  const [deleting, setDeleting] = useState<Role | null>(null);

  return (
    <>
      <PageHeader
        crumb="HRM"
        title="Roles"
        subtitle="Protected seed roles (admin/sales/staff/readonly) cannot be renamed or deleted."
        actions={
          <Button variant="primary" icon="plus" onClick={() => setEditing('new')}>
            New role
          </Button>
        }
      />
      {crud.list.isLoading && <div className="card card-body muted">Loading…</div>}
      {crud.list.error && <div className="alert">{(crud.list.error as Error).message}</div>}
      {crud.list.data && (
        <div className="card">
          <table className="table">
            <thead>
              <tr>
                <th className="col-num">#</th>
                <th>Name</th>
                <th>Slug</th>
                <th>Description</th>
                <th style={{ width: 60 }} />
              </tr>
            </thead>
            <tbody>
              {crud.list.data.data.map((row, i) => {
                const protectedRow = PROTECTED.has(row.slug);
                return (
                  <tr key={row.id}>
                    <td className="col-num">{i + 1}</td>
                    <td>
                      {row.name}
                      {protectedRow && (
                        <span
                          className="badge gray"
                          style={{ marginLeft: 8, fontSize: 10 }}
                          title="Seed role — slug protected"
                        >
                          system
                        </span>
                      )}
                    </td>
                    <td className="cell-mono">{row.slug}</td>
                    <td className="muted">{row.description ?? '—'}</td>
                    <td>
                      <RowMenu
                        items={[
                          { icon: 'edit', label: 'Edit', onClick: () => setEditing(row) },
                          ...(protectedRow
                            ? []
                            : [
                                'divider' as const,
                                {
                                  icon: 'trash' as const,
                                  label: 'Delete',
                                  onClick: () => setDeleting(row),
                                  danger: true,
                                },
                              ]),
                        ]}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <EditModal
          row={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(null)}
          onSubmit={async (body) => {
            if (editing === 'new') await crud.create.mutateAsync(body);
            else await crud.update.mutateAsync({ id: editing.id, body });
            setEditing(null);
          }}
        />
      )}

      <ConfirmDialog
        open={Boolean(deleting)}
        title="Delete role"
        message={deleting ? `Delete role "${deleting.name}"?` : null}
        destructive
        confirmLabel="Delete"
        loading={crud.remove.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          if (!deleting) return;
          await crud.remove.mutateAsync(deleting.id);
          setDeleting(null);
        }}
      />
    </>
  );
}

function EditModal({
  row,
  onClose,
  onSubmit,
}: {
  row?: Role;
  onClose: () => void;
  onSubmit: (b: ReturnType<typeof toBody>) => Promise<void>;
}) {
  const [form, setForm] = useState(() => toForm(row));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const slugLocked = row ? PROTECTED.has(row.slug) : false;

  async function handle(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await onSubmit(toBody(form));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Save failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open onClose={onClose} title={row ? 'Edit role' : 'New role'} width={480}>
      <form onSubmit={handle}>
        <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Input
            id="role-name"
            label="Name"
            value={form.name}
            onChange={(v) => setForm({ ...form, name: v })}
            required
            autoFocus
          />
          <Input
            id="role-slug"
            label="Slug"
            value={form.slug}
            onChange={(v) => setForm({ ...form, slug: v })}
            required
            disabled={slugLocked}
          />
          {slugLocked && (
            <div className="muted" style={{ fontSize: 12 }}>
              System role — slug cannot be changed.
            </div>
          )}
          <div>
            <label className="field-label" htmlFor="role-desc">
              Description
            </label>
            <textarea
              id="role-desc"
              className="input"
              rows={3}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </div>
          {error && <div className="alert">{error}</div>}
        </div>
        <div
          className="card-header"
          style={{ justifyContent: 'flex-end', borderTop: '1px solid var(--border)', borderBottom: 'none' }}
        >
          <Button onClick={onClose} disabled={submitting} type="button">
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
